/**
 * Firestore Service Layer — React hooks for reading booking data
 *
 * Provides hooks that abstract Firestore queries so components never
 * import Firebase directly. All reads use real-time listeners (onSnapshot)
 * so the UI stays in sync without manual refetching.
 */
import { useState, useEffect, useRef } from 'react';
import { mergeWithDefaults } from '../data/defaultTemplates';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

/** Normalize phone to E.164 (+1XXXXXXXXXX) for US numbers */
function normalizePhone(raw) {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (raw.startsWith('+')) return raw.trim();
  return raw.trim();
}

/**
 * useClassCards — subscribe to all shown class cards
 * Returns { cards, loading, error }
 */
export function useClassCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // No orderBy — avoids requiring a composite index.
    // ClassGrid sorts cards by availability (bookable → inquiry → coming soon).
    const q = query(collection(db, 'classCards'), where('shown', '==', true));

    const unsub = onSnapshot(
      q,
      (snap) => {
        setCards(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('useClassCards error:', err);
        setError(err);
        setLoading(false);
      },
    );

    return unsub;
  }, []);

  return { cards, loading, error };
}

/**
 * useAllClassCards — subscribe to ALL cards (shown + hidden) for admin
 * Returns { cards, loading, error }
 */
export function useAllClassCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'classCards'), orderBy('sortOrder', 'asc'));

    const unsub = onSnapshot(
      q,
      (snap) => {
        setCards(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('useAllClassCards error:', err);
        setError(err);
        setLoading(false);
      },
    );

    return unsub;
  }, []);

  return { cards, loading, error };
}

/**
 * useSessions — subscribe to open future sessions for a specific class card
 * Returns { sessions, loading, error }
 */
export function useSessions(cardId) {
  const [sessions, setSessions] = useState([]);
  const [soldOut, setSoldOut] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!cardId) {
      setSessions([]);
      setLoading(false);
      return;
    }

    // Single-field equality query — avoids composite index requirement.
    // Status and date are filtered client-side; results sorted client-side.
    const q = query(
      collection(db, 'sessions'),
      where('classCardId', '==', cardId),
    );

    const today = new Date().toISOString().split('T')[0];
    const unsub = onSnapshot(
      q,
      (snap) => {
        const all = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((s) => s.date >= today && s.status !== 'cancelled')
          .sort((a, b) => {
            const d = a.date.localeCompare(b.date);
            return d !== 0
              ? d
              : (a.startTime || '').localeCompare(b.startTime || '');
          });
        const open = all.filter((s) => s.status === 'open');
        const hasFull = all.some((s) => s.status === 'full');
        // When at least one session is bookable, show every future
        // session (open + sold-out) in chronological order so sold-out
        // dates stay visible for marketing. When nothing is bookable,
        // return empty so the sold-out inquiry / waitlist flow is unchanged.
        setSessions(open.length > 0 ? all : []);
        setSoldOut(open.length === 0 && hasFull);
        setLoading(false);
      },
      (err) => {
        console.error('useSessions error:', err);
        setError(err);
        setLoading(false);
      },
    );

    return unsub;
  }, [cardId]);

  return { sessions, soldOut, loading, error };
}

/**
 * usePublicSessions — subscribe to all upcoming open sessions (public-facing)
 * Returns { sessionCardIds: Set<string>, loading }
 * Used by ClassGrid to determine which cards have bookable sessions.
 */
export function usePublicSessions() {
  const [sessionCardIds, setSessionCardIds] = useState(new Set());
  const [soldOutCardIds, setSoldOutCardIds] = useState(new Set());
  const [pastSessionCardIds, setPastSessionCardIds] = useState(new Set());
  const [earliestSessionDate, setEarliestSessionDate] = useState(new Map()); // cardId → 'YYYY-MM-DD'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];

    // Future sessions — open vs full
    const futureQ = query(
      collection(db, 'sessions'),
      where('date', '>=', today),
    );

    // All sessions — to detect cards that had past sessions
    const allQ = query(collection(db, 'sessions'));

    let futureData = null;
    let allData = null;
    let futureReady = false;
    let allReady = false;

    const reconcile = () => {
      if (!futureReady || !allReady) return;
      const openIds = new Set();
      const fullIds = new Set();
      const earliestMap = new Map(); // cardId → earliest open session date
      futureData.docs.forEach((d) => {
        const data = d.data();
        if (data.status === 'open') {
          openIds.add(data.classCardId);
          const cur = earliestMap.get(data.classCardId);
          if (!cur || data.date < cur)
            earliestMap.set(data.classCardId, data.date);
        }
        if (data.status === 'full') fullIds.add(data.classCardId);
      });
      setSessionCardIds(openIds);
      setSoldOutCardIds(new Set([...fullIds].filter((id) => !openIds.has(id))));
      setEarliestSessionDate(earliestMap);

      // Past-only = had sessions but none are future-open or future-full
      const allCardIds = new Set();
      allData.docs.forEach((d) => {
        const data = d.data();
        if (data.status !== 'cancelled') allCardIds.add(data.classCardId);
      });
      setPastSessionCardIds(
        new Set(
          [...allCardIds].filter((id) => !openIds.has(id) && !fullIds.has(id)),
        ),
      );
      setLoading(false);
    };

    const unsubFuture = onSnapshot(
      futureQ,
      (snap) => {
        futureData = snap;
        futureReady = true;
        reconcile();
      },
      (err) => {
        console.error('usePublicSessions future error:', err);
        futureReady = true;
        futureData = { docs: [] };
        reconcile();
      },
    );

    const unsubAll = onSnapshot(
      allQ,
      (snap) => {
        allData = snap;
        allReady = true;
        reconcile();
      },
      (err) => {
        console.error('usePublicSessions all error:', err);
        allReady = true;
        allData = { docs: [] };
        reconcile();
      },
    );

    return () => {
      unsubFuture();
      unsubAll();
    };
  }, []);

  return {
    sessionCardIds,
    soldOutCardIds,
    pastSessionCardIds,
    earliestSessionDate,
    loading,
  };
}

/**
 * createBooking — transactionally create a booking and increment spotsBooked
 * Returns the new booking document ID.
 */
export async function createBooking({
  session,
  card,
  seats,
  customerName,
  customerEmail,
  customerPhone,
}) {
  const sessionRef = doc(db, 'sessions', session.id);
  const bookingsRef = collection(db, 'bookings');

  let bookingId;

  await runTransaction(db, async (tx) => {
    const sessionSnap = await tx.get(sessionRef);
    if (!sessionSnap.exists()) throw new Error('Session not found');

    const data = sessionSnap.data();
    const booked = data.spotsBooked || 0;
    const total = data.spotsTotal || 0;
    const remaining = total - booked;

    if (remaining < seats) throw new Error('Not enough spots remaining');

    const bookingRef = doc(bookingsRef);
    bookingId = bookingRef.id;

    tx.set(bookingRef, {
      classCardId: card.id,
      cardTitle: card.title,
      sessionId: session.id,
      sessionDate: session.date,
      sessionStartTime: session.startTime,
      sessionEndTime: session.endTime,
      customerName,
      customerEmail,
      customerPhone: normalizePhone(customerPhone),
      seats,
      pricePerSeat: session.price || 0,
      totalPrice: (session.price || 0) * seats,
      status: 'confirmed',
      paymentStatus: 'pay-at-studio',
      createdAt: serverTimestamp(),
    });

    const newBooked = booked + seats;
    const newRemaining = total - newBooked;
    tx.update(sessionRef, {
      spotsBooked: newBooked,
      spotsRemaining: newRemaining,
      ...(newRemaining <= 0 ? { status: 'full' } : {}),
    });
  });

  // Upsert customer record (non-critical — don't throw if this fails)
  try {
    const upsertCustomer = httpsCallable(functions, 'upsertCustomer');
    await upsertCustomer({
      email: customerEmail,
      name: customerName,
      phone: normalizePhone(customerPhone),
      bookingId,
    });
  } catch (err) {
    console.error('Customer upsert failed (non-critical):', err);
  }

  return bookingId;
}

/**
 * createManualBooking — admin-created booking (walk-in or manual entry)
 * Transactionally creates the booking, decrements spots, upserts customer.
 * Returns the new booking document ID.
 */
export async function createManualBooking({
  session,
  customerName,
  customerEmail,
  customerPhone,
  seats,
  paymentStatus,
  notes,
  isWalkIn,
}) {
  const sessionRef = doc(db, 'sessions', session.id);
  const bookingsRef = collection(db, 'bookings');

  let bookingId;

  await runTransaction(db, async (tx) => {
    const sessionSnap = await tx.get(sessionRef);
    if (!sessionSnap.exists()) throw new Error('Session not found');

    const data = sessionSnap.data();
    const booked = data.spotsBooked || 0;
    const remaining = (data.spotsTotal || 0) - booked;

    if (remaining < seats)
      throw new Error(`Only ${remaining} spot(s) remaining`);

    const bookingRef = doc(bookingsRef);
    bookingId = bookingRef.id;

    tx.set(bookingRef, {
      classCardId: session.classCardId || '',
      cardTitle: session.cardTitle || '',
      sessionId: session.id,
      sessionDate: session.date,
      sessionStartTime: session.startTime,
      sessionEndTime: session.endTime,
      customerName,
      customerEmail,
      customerPhone: normalizePhone(customerPhone),
      seats,
      pricePerSeat: session.price || 0,
      totalPrice: (session.price || 0) * seats,
      status: 'confirmed',
      paymentStatus: paymentStatus || 'unpaid',
      notes: notes || '',
      isWalkIn: isWalkIn ?? true,
      bookingSource: isWalkIn ? 'walk-in' : 'admin',
      createdAt: serverTimestamp(),
      confirmationSent: false,
      reminderSent: false,
    });

    const newBooked = booked + seats;
    const newRemaining = (data.spotsTotal || 0) - newBooked;
    tx.update(sessionRef, {
      spotsBooked: newBooked,
      spotsRemaining: newRemaining,
      ...(newRemaining <= 0 ? { status: 'full' } : {}),
    });
  });

  // Upsert customer record (non-critical, fire-and-forget to avoid cold-start delay)
  const upsertCustomer = httpsCallable(functions, 'upsertCustomer');
  upsertCustomer({
    email: customerEmail,
    name: customerName,
    phone: normalizePhone(customerPhone),
    bookingId,
  }).catch((err) =>
    console.error('Customer upsert failed (non-critical):', err),
  );

  // Complimentary confirmation is handled by the caller (SessionDetailPanel)
  // so it can show visible UI feedback. All other bookings wait for payment
  // to be collected (via sendBookingConfirmation in CollectPaymentModal).

  return bookingId;
}

/**
 * useAllSessions — subscribe to ALL sessions across all cards (for admin calendar)
 * Returns { sessions, loading, error }
 */
export function useAllSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'sessions'), orderBy('date', 'asc'));

    const unsub = onSnapshot(
      q,
      (snap) => {
        setSessions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('useAllSessions error:', err);
        setError(err);
        setLoading(false);
      },
    );

    return unsub;
  }, []);

  return { sessions, loading, error };
}

/**
 * useBookings — subscribe to bookings, optionally filtered by sessionRef or date range
 * Returns { bookings, loading, error }
 */
export function useBookings(filters = {}) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let q = collection(db, 'bookings');
    const constraints = [];

    if (filters.sessionRef) {
      constraints.push(where('sessionRef', '==', filters.sessionRef));
    }
    if (filters.startDate) {
      constraints.push(where('createdAt', '>=', new Date(filters.startDate)));
    }
    if (filters.endDate) {
      constraints.push(where('createdAt', '<=', new Date(filters.endDate)));
    }

    constraints.push(orderBy('createdAt', 'desc'));

    const finalQuery = query(q, ...constraints);

    const unsub = onSnapshot(
      finalQuery,
      (snap) => {
        setBookings(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate?.() || null,
          })),
        );
        setLoading(false);
      },
      (err) => {
        console.error('useBookings error:', err);
        setError(err);
        setLoading(false);
      },
    );

    return unsub;
  }, [filters.sessionRef, filters.startDate, filters.endDate]);

  return { bookings, loading, error };
}

/**
 * useTemplates — subscribe to all templates
 * Returns:
 *   templates         — DEFAULT_TEMPLATES merged with any Firestore overrides
 *   firestoreTemplates — raw Firestore docs only (used to detect overrides)
 *   loading, error
 */
export function useTemplates() {
  const [templates, setTemplates] = useState([]);
  const [firestoreTemplates, setFirestoreTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'templates'),
      (snap) => {
        const firestoreDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setFirestoreTemplates(firestoreDocs);
        setTemplates(mergeWithDefaults(firestoreDocs));
        setLoading(false);
      },
      (err) => {
        console.error('useTemplates error:', err);
        setError(err);
        setLoading(false);
      },
    );

    return unsub;
  }, []);

  return { templates, firestoreTemplates, loading, error };
}

/**
 * useTemplateDefaults — subscribe to templateDefaults collection
 *
 * Returns a map of templateId → { title, tagline, description, blobColor,
 * location, designOptions[], whatsIncluded[], rules[] }
 *
 * Used by ClassCardsManager to prefill new class cards and by
 * ClassDefaultsManager to edit defaults from the admin dashboard.
 */
export function useTemplateDefaults() {
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'templateDefaults'),
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          map[d.id] = d.data();
        });
        setDefaults(map);
        setLoading(false);
      },
      (err) => {
        console.error('useTemplateDefaults error:', err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { defaults, loading };
}

/**
 * useLocations — subscribe to all locations, split into active + deleted
 */
export function useLocations() {
  const [locations, setLocations] = useState([]);
  const [deletedLocations, setDeletedLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'locations'), orderBy('name')),
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLocations(all.filter((l) => l.status !== 'deleted'));
        setDeletedLocations(all.filter((l) => l.status === 'deleted'));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  return { locations, deletedLocations, loading };
}

/**
 * getClassCard — fetch a single class card by document ID
 */
export async function getClassCard(cardId) {
  const snap = await getDoc(doc(db, 'classCards', cardId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * useAllSessionsAdmin — subscribe to ALL sessions with full card metadata (incl. color)
 * Returns { sessions, loading }
 * Each session has: id, classCardId, cardTitle, cardLocation, cardColor, templateId,
 *   date, startTime, endTime, spotsTotal, spotsBooked, spotsRemaining, status
 *
 * Uses two independent onSnapshot listeners that merge on every change:
 *   1. /classCards — for card metadata (title, color, location, templateId)
 *   2. /sessions   — top-level collection, all sessions
 * Refs hold the latest snapshot from each side so merges are always consistent.
 */
export function useAllSessionsAdmin() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const cardsMetaRef = useRef({}); // cardId → metadata
  const rawSessionsRef = useRef([]); // raw session docs
  const readyRef = useRef({ cards: false, sessions: false });

  useEffect(() => {
    const doMerge = () => {
      if (!readyRef.current.cards || !readyRef.current.sessions) return;
      const all = rawSessionsRef.current.map((s) => ({
        ...(cardsMetaRef.current[s.classCardId] || {}),
        ...s,
      }));
      all.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      setSessions(all);
      setLoading(false);
    };

    // ── 1. Listen to classCards for metadata ──────────────────────────────
    const unsubCards = onSnapshot(
      collection(db, 'classCards'),
      (snap) => {
        const meta = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          meta[d.id] = {
            cardId: d.id,
            cardTitle: data.title,
            cardLocation: data.location || '',
            cardColor: data.blobColor || data.color || '#E8A020',
            templateId: data.templateId,
            cardVisibility:
              data.visibility ||
              ((data.tag || '').includes('Private') ? 'Private' : 'Public'),
          };
        });
        cardsMetaRef.current = meta;
        readyRef.current.cards = true;
        doMerge();
      },
      (err) => {
        console.error('useAllSessionsAdmin cards error:', err);
        setLoading(false);
      },
    );

    // ── 2. Listen to top-level sessions collection ─────────────────────────
    const unsubSessions = onSnapshot(
      query(collection(db, 'sessions'), orderBy('date', 'asc')),
      (snap) => {
        rawSessionsRef.current = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        readyRef.current.sessions = true;
        doMerge();
      },
      (err) => {
        console.error('useAllSessionsAdmin sessions error:', err);
        setLoading(false);
      },
    );

    return () => {
      unsubCards();
      unsubSessions();
    };
  }, []);

  return { sessions, loading };
}

/**
 * useCustomers — subscribe to /customers collection as source of truth
 * Returns { customers (array), loading }
 * Each customer has: id, customerId, email, name, firstName, lastName, phone,
 * notes, bookingIds, bookingCount, possibleDuplicate, possibleDuplicateOf,
 * possibleDuplicateReason, createdAt, updatedAt
 */
export function useCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'customers'),
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCustomers(list);
        setLoading(false);
      },
      (err) => {
        console.error('useCustomers error:', err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { customers, loading };
}

/**
 * useCustomerProfiles — subscribe to /customers collection (stored notes/edits)
 * Returns { profiles (object keyed by email), loading }
 */
export function useCustomerProfiles() {
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'customers'),
      (snap) => {
        const obj = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          const key = data.email || d.id;
          obj[key] = { id: d.id, ...data };
        });
        setProfiles(obj);
        setLoading(false);
      },
      (err) => {
        console.error('useCustomerProfiles error:', err);
        setLoading(false);
      },
    );

    return unsub;
  }, []);

  return { profiles, loading };
}

/**
 * useAboutContent — subscribe to siteContent/aboutArtist document
 * Returns { content, loading, error }
 * Defaults are used if the document doesn't exist yet.
 */
const ABOUT_DEFAULTS = {
  quote:
    'Feminine forms, expressive vessels — handmade ceramics meant for everyday rituals.',
  bio: 'Viri Aviña is a Dallas-based ceramic artist and instructor. Her work explores feminine forms, expressive vessels, and handmade ceramics meant for everyday rituals. Through her workshops, she introduces beginners to pottery in a relaxed and welcoming environment where creativity and curiosity are encouraged.',
  tagline: 'Dallas, Texas · Luz y Tierra · Private Home Studio Sessions',
  image1Url: '/artist.JPG',
  image2Url: '/artist profile 2.jpg',
};

export function useAboutContent() {
  const [content, setContent] = useState(ABOUT_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'siteContent', 'aboutArtist'),
      (snap) => {
        if (snap.exists()) {
          setContent({ ...ABOUT_DEFAULTS, ...snap.data() });
        } else {
          setContent(ABOUT_DEFAULTS);
        }
        setLoading(false);
      },
      (err) => {
        console.error('useAboutContent error:', err);
        setError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { content, loading, error };
}

/**
 * useHomepageContent — subscribe to siteContent/homepage document
 * Returns { content, loading, error }
 * Defaults are used if the document doesn't exist yet.
 */
const HOMEPAGE_DEFAULTS = {
  heroTagline: 'Ceramic Art & Workshops',
  heroHeadline:
    'Crafting handmade pottery and inspiring creativity in every piece.',
  heroButtonText: 'Explore Classes',
  introTitle: 'Pottery Workshops',
  introDescription:
    'Beginner-friendly hand-building workshops where participants create ceramic pieces — mugs, vases, planters, and more — in a relaxed and welcoming studio environment. No prior pottery experience required. Perfect for individuals, couples, and small groups looking for a creative activity in Dallas.',
  pillar1Label: 'Beginner Friendly',
  pillar1Note: 'No prior experience required',
  pillar2Label: 'Dallas, Texas',
  pillar2Note: 'Luz y Tierra & private studio',
  pillar3Label: 'Small Groups',
  pillar3Note: 'Hands-on guidance throughout',
};

export function useHomepageContent() {
  const [content, setContent] = useState(HOMEPAGE_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'siteContent', 'homepage'),
      (snap) => {
        if (snap.exists()) {
          setContent({ ...HOMEPAGE_DEFAULTS, ...snap.data() });
        } else {
          setContent(HOMEPAGE_DEFAULTS);
        }
        setLoading(false);
      },
      (err) => {
        console.error('useHomepageContent error:', err);
        setError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { content, loading, error };
}

/**
 * useStudioTilesContent — subscribe to siteContent/studioTiles document
 * Returns { tiles, loading, error }
 * tiles = { handBuilding: {...}, wheelThrowing: {...} }
 */
const EMPTY_PHOTOS = (captions) =>
  captions.map((caption) => ({ url: '', caption }));

const STUDIO_DEFAULTS = {
  handBuilding: {
    carouselLabel: 'At the Studio',
    carouselTitle: 'Hand Building',
    carouselSub: 'Build freely — no wheel needed. Just hands and clay.',
    buttonText: 'View Gallery',
    tag: 'Public Pottery\nWorkshop',
    galleryDesc:
      "Hand building is one of the oldest forms of pottery — no wheel required. Working directly with your hands and simple tools, you'll use three core techniques to shape clay into something uniquely yours. Pinch forms a piece by pressing and rotating the clay between your fingers. Coiling builds up walls by stacking and blending ropes of clay. Slab construction cuts and joins flat sheets of clay into geometric forms. Each method is beginner-friendly and taught step by step in a relaxed, hands-on environment.",
    photos: EMPTY_PHOTOS([
      'Setting the clay',
      'Shaping by hand',
      'Building the walls',
      'Adding texture',
      'Finishing touches',
      'Final pieces',
    ]),
    ctaHeading: 'Ready to get your hands dirty?',
    ctaLocation: 'Luz y Tierra, Mesquite',
    ctaDuration: '2 hrs',
    ctaButtonText: 'Reserve a Spot',
  },
  wheelThrowing: {
    carouselLabel: 'At the Studio',
    carouselTitle: 'Wheel Throwing',
    carouselSub:
      'Enjoy a personalized, hands-on experience in this 1-on-1 wheel throwing class held at my home studio.',
    buttonText: 'View Gallery',
    tag: 'Private\nOne on One',
    galleryDesc:
      "Wheel throwing is the art of shaping clay on a spinning wheel — one of pottery's most iconic forms. Sessions begin with wedging and centering the clay on the wheel head, learning to feel and control it with your hands. From there, you'll open the clay to create a floor, then pull the walls upward into a cylinder or vessel form. Each step builds muscle memory and presence. Private sessions focus on your pace, your hands, and the fundamentals that make every piece come alive.",
    photos: EMPTY_PHOTOS([
      'Centering the clay',
      'Opening the form',
      'Pulling the walls',
      'Shaping the vessel',
      'Trimming the base',
      'Off the wheel',
    ]),
    ctaHeading: 'Ready to get your hands dirty?',
    ctaLocation: 'Home Studio, Dallas',
    ctaDuration: '2 hrs',
    ctaButtonText: 'Reserve a Spot',
  },
};

export function useStudioTilesContent() {
  const [tiles, setTiles] = useState(STUDIO_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'siteContent', 'studioTiles'),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setTiles({
            handBuilding: {
              ...STUDIO_DEFAULTS.handBuilding,
              ...(data.handBuilding || {}),
            },
            wheelThrowing: {
              ...STUDIO_DEFAULTS.wheelThrowing,
              ...(data.wheelThrowing || {}),
            },
          });
        } else {
          setTiles(STUDIO_DEFAULTS);
        }
        setLoading(false);
      },
      (err) => {
        console.error('useStudioTilesContent error:', err);
        setError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { tiles, loading, error };
}

/**
 * useFAQContent — subscribe to siteContent/faq document
 * Returns { faq, loading, error }
 * faq = { tile: {...}, page: {...}, items: [...] }
 */
const FAQ_DEFAULTS = {
  tile: {
    label: 'Before You Arrive',
    heading: 'Workshop Details & FAQ',
    description:
      "What to wear, what you'll make, when to pick it up — everything you need to know before your first session.",
    teaserQuestions: [
      'Do I need experience?',
      'What will I make?',
      'How long does a session last?',
      'When can I pick up my piece?',
    ],
    buttonText: 'Browse FAQ',
  },
  page: {
    label: 'Before You Arrive',
    heading: 'WORKSHOP DETAILS & FAQ',
    description:
      "Everything you need to know before your first session — from what to wear to what you'll create and when to pick it up.",
  },
  items: [
    {
      q: 'Do I need pottery experience?',
      a: 'No experience required. All workshops are beginner-friendly and designed for anyone curious about working with clay.',
      bullets: [],
      note: '',
    },
    {
      q: 'What will I make?',
      a: 'Depending on the workshop, you may create:',
      bullets: ['Mug', 'Vase', 'Planter', 'Custom project (select workshops)'],
      note: 'Project options vary by workshop type.',
    },
    {
      q: 'How long are workshops?',
      a: 'Most workshops run 2–3 hours, including a short demonstration, guided building time, and one-on-one assistance throughout.',
      bullets: [],
      note: '',
    },
    {
      q: "What's included?",
      a: 'All workshops include:',
      bullets: [
        'Clay and tools',
        'Step-by-step instruction',
        'Glazing and kiln firing of your finished piece',
      ],
      note: '',
    },
    {
      q: 'When will my piece be ready?',
      a: "Pottery must dry and go through multiple kiln firings before it's finished. Pieces are typically ready for pickup 3–4 weeks after the workshop — you'll be notified when yours is ready.",
      bullets: [],
      note: '',
    },
    {
      q: 'What should I wear?',
      a: "Comfortable clothing you don't mind getting dusty or messy. Working with clay can get a little dirty.",
      bullets: [],
      note: '',
    },
    {
      q: 'Should I arrive early?',
      a: 'Please arrive 10 minutes before the workshop begins so the class can start on time. Late arrivals may miss the opening demonstration.',
      bullets: [],
      note: '',
    },
    {
      q: 'Can I bring friends?',
      a: 'Absolutely — many participants attend with friends or small groups. Each person must have a reserved spot.',
      bullets: [],
      note: '',
    },
    {
      q: "What's your cancellation policy?",
      a: "Workshop bookings are non-refundable due to limited seating. If you're unable to attend, you may transfer your spot to another guest.",
      bullets: [],
      note: '',
    },
    {
      q: 'When should I pick up my piece?',
      a: 'Please pick up your finished piece within 30 days of notification. Due to limited studio storage, pieces not collected within that window may be discarded.',
      bullets: [],
      note: '',
    },
    {
      q: 'What if my piece cracks during firing?',
      a: 'Clay is a natural material and pieces can occasionally crack or break during drying or firing. While every effort is made to protect your work, this is a normal part of the ceramic process.',
      bullets: [],
      note: '',
    },
    {
      q: 'Where are workshops held?',
      a: 'Workshops are hosted at:',
      bullets: [
        'Luz y Tierra — 2828 I-30 Frontage Rd, Suite 101, Mesquite, Texas',
        'Home Studio — Dallas, Texas',
      ],
      note: 'Some workshops may also be hosted at private venues in the Dallas area.',
    },
    {
      q: 'Do you offer private workshops or events?',
      a: 'Yes — private workshops are available for groups and special events, perfect for birthdays, date nights, team activities, and creative gatherings.',
      bullets: [
        'Luz y Tierra: 10–25 guests',
        'Home Studio in Dallas: 4–10 guests',
        'Travel workshops within the Dallas area (setup and travel fees may apply)',
      ],
      note: '',
    },
    {
      q: 'Do you offer one-on-one wheel throwing lessons?',
      a: 'Yes, on a limited basis. These sessions are scheduled individually and must be arranged by inquiry.',
      bullets: [],
      note: '',
    },
  ],
};

export function useFAQContent() {
  const [faq, setFaq] = useState(FAQ_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'siteContent', 'faq'),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setFaq({
            tile: { ...FAQ_DEFAULTS.tile, ...(data.tile || {}) },
            page: { ...FAQ_DEFAULTS.page, ...(data.page || {}) },
            items: data.items || FAQ_DEFAULTS.items,
          });
        } else {
          setFaq(FAQ_DEFAULTS);
        }
        setLoading(false);
      },
      (err) => {
        console.error('useFAQContent error:', err);
        setError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { faq, loading, error };
}

/**
 * useShopContent — subscribe to siteContent/shop document
 * Returns { shop, loading, error }
 * shop = { tile: {...}, page: {...} }
 * Note: product data (name, price, stock, photos) is managed by Square.
 */
const SHOP_DEFAULTS = {
  tile: {
    label: 'The Collection',
    heading: 'Artisan Ceramics',
    description:
      'Each piece is hand-built in the studio — bowls, vessels, and decorative objects shaped from raw clay and finished with natural glazes. One of a kind, made to live with.',
    teaserItems: [
      'Hand-built pottery',
      'Pinch bowls & serving dishes',
      'Ceramic vases',
    ],
    buttonText: 'View Collection',
  },
  page: {
    label: 'The Collection',
    heading: 'CERAMIC PIECES',
    description:
      'Each piece is hand-built from raw clay — shaped, bisque-fired, glazed, and fired again. No two are exactly alike. Ships carefully from Dallas, Texas.',
  },
};

export function useShopContent() {
  const [shop, setShop] = useState(SHOP_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'siteContent', 'shop'),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setShop({
            tile: { ...SHOP_DEFAULTS.tile, ...(data.tile || {}) },
            page: { ...SHOP_DEFAULTS.page, ...(data.page || {}) },
          });
        } else {
          setShop(SHOP_DEFAULTS);
        }
        setLoading(false);
      },
      (err) => {
        console.error('useShopContent error:', err);
        setError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { shop, loading, error };
}

/**
 * useNewsletterContent — subscribe to siteContent/newsletter document
 * Returns { newsletter, loading, error }
 */
const NEWSLETTER_DEFAULTS = {
  label: 'Stay Close',
  heading: 'Get Updates On New Classes',
  description: 'New sessions added monthly. Be the first to know.',
  firstNamePlaceholder: 'First name',
  emailPlaceholder: 'Email address',
  buttonText: 'Get in Touch',
  successHeading: "You're on the list!",
  successMessage:
    "Thanks, {name}! We'll let you know when new sessions open up.",
};

export function useNewsletterContent() {
  const [newsletter, setNewsletter] = useState(NEWSLETTER_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'siteContent', 'newsletter'),
      (snap) => {
        if (snap.exists()) {
          setNewsletter({ ...NEWSLETTER_DEFAULTS, ...snap.data() });
        } else {
          setNewsletter(NEWSLETTER_DEFAULTS);
        }
        setLoading(false);
      },
      (err) => {
        console.error('useNewsletterContent error:', err);
        setError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { newsletter, loading, error };
}

/**
 * useCardBySlug — fetch a single class card by its slug field (one-time read)
 * Used by the /b/:slug route to resolve a human-readable URL to a card ID.
 * Returns { card, loading, error }
 */
export function useCardBySlug(slug) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    const q = query(collection(db, 'classCards'), where('slug', '==', slug));
    getDocs(q)
      .then((snap) => {
        if (snap.empty) {
          setError('not-found');
        } else {
          const d = snap.docs[0];
          setCard({ id: d.id, ...d.data() });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('useCardBySlug error:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  return { card, loading, error };
}

// ── useInquiryForm ─────────────────────────────────────────────────────────
// Real-time listener for a single inquiry form config doc
export function useInquiryForm(formId) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!formId) {
      setLoading(false);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'inquiryForms', formId),
      (snap) => {
        setForm(snap.exists() ? snap.data() : null);
        setLoading(false);
      },
      (err) => {
        console.error('useInquiryForm error:', err);
        setLoading(false);
      },
    );
    return unsub;
  }, [formId]);

  return { form, loading };
}

/**
 * useNotificationTemplates — subscribe to all notification template overrides
 * Returns { templates, loading }
 * templates is a map: { [triggerId]: { id, enabled, subject, bodyHtml, smsEnabled, smsBody, updatedAt } }
 */
export function useNotificationTemplates() {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'notificationTemplates'),
      (snap) => {
        const data = {};
        snap.docs.forEach((d) => {
          data[d.id] = { id: d.id, ...d.data() };
        });
        setTemplates(data);
        setLoading(false);
      },
      (err) => {
        console.error('useNotificationTemplates error:', err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { templates, loading };
}

/**
 * saveNotificationTemplate — write (merge) fields to notificationTemplates/{id}
 */
export async function saveNotificationTemplate(id, fields) {
  await setDoc(
    doc(db, 'notificationTemplates', id),
    {
      ...fields,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/**
 * deleteNotificationTemplate — remove override, functions fall back to built-in default
 */
export async function deleteNotificationTemplate(id) {
  await deleteDoc(doc(db, 'notificationTemplates', id));
}

/**
 * useNewsletter — subscribe to the /newsletter collection
 * Returns { newsletterMap, loading }
 * newsletterMap: object keyed by email (lowercase) with { optedOut, subscribedAt, ... }
 */
export function useNewsletter() {
  const [newsletterMap, setNewsletterMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'newsletter'),
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          map[d.id] = d.data();
        });
        setNewsletterMap(map);
        setLoading(false);
      },
      (err) => {
        console.error('useNewsletter error:', err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { newsletterMap, loading };
}
